import {
  applicationResponseSchema,
  applicationsResponseSchema,
  authSessionSchema,
  type CreateApplication,
  type CreateRelease,
  deleteResponseSchema,
  logoutResponseSchema,
  releaseResponseSchema,
  releasesResponseSchema,
  type UpdateApplication,
  type UpdateRelease,
} from "@updaterelay/contracts";
import type { ZodType } from "zod";

const defaultApiUrl = import.meta.env.PROD
  ? "https://relay-api.apps.jacklabbe.com"
  : "http://localhost:8787";

export const API_BASE_URL = (import.meta.env.VITE_API_URL ?? defaultApiUrl).replace(/\/$/u, "");

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "request_failed",
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

interface RequestOptions extends RequestInit {
  csrfToken?: string;
}

async function request<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body) headers.set("Content-Type", "application/json");
  if (options.csrfToken) headers.set("X-CSRF-Token", options.csrfToken);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });
  const payload = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;

  if (!response.ok) {
    throw new ApiRequestError(
      payload?.error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      payload?.error?.code,
    );
  }

  return schema.parse(payload);
}

export const api = {
  getSession: () => request("/v1/auth/session", authSessionSchema),
  getApplications: () => request("/v1/admin/applications", applicationsResponseSchema),
  createApplication: (input: CreateApplication, csrfToken: string) =>
    request("/v1/admin/applications", applicationResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
      csrfToken,
    }),
  updateApplication: (id: string, input: UpdateApplication, csrfToken: string) =>
    request(`/v1/admin/applications/${id}`, applicationResponseSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
      csrfToken,
    }),
  deleteApplication: (id: string, csrfToken: string) =>
    request(`/v1/admin/applications/${id}`, deleteResponseSchema, {
      method: "DELETE",
      csrfToken,
    }),
  getReleases: (applicationId: string) =>
    request(`/v1/admin/applications/${applicationId}/releases`, releasesResponseSchema),
  createRelease: (applicationId: string, input: CreateRelease, csrfToken: string) =>
    request(`/v1/admin/applications/${applicationId}/releases`, releaseResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
      csrfToken,
    }),
  updateRelease: (
    applicationId: string,
    releaseId: string,
    input: UpdateRelease,
    csrfToken: string,
  ) =>
    request(
      `/v1/admin/applications/${applicationId}/releases/${releaseId}`,
      releaseResponseSchema,
      {
        method: "PATCH",
        body: JSON.stringify(input),
        csrfToken,
      },
    ),
  deleteRelease: (applicationId: string, releaseId: string, csrfToken: string) =>
    request(`/v1/admin/applications/${applicationId}/releases/${releaseId}`, deleteResponseSchema, {
      method: "DELETE",
      csrfToken,
    }),
  logout: (csrfToken: string) =>
    request("/v1/auth/logout", logoutResponseSchema, { method: "POST", csrfToken }),
};
